import { Instance, SnapshotOut, types } from "mobx-state-tree"
import { withEnvironment } from "../extensions/with-environment"
import { withRootStore } from "../extensions/with-root-store"
import { getGeneralApiProblem } from "../../services/api/api-problem"
import moment from "moment"
import _ from 'lodash';
import { Simulation } from "../../database"
import { Platform } from "react-native"
import { Toast } from "native-base"
import { BaseModelStoreProps } from "../base-model-store/base-model-store"
import { withBaseModel } from "../extensions/with-base-model"
import JalaAnalytic from "../../utils/jala-analytic"

const SimulationStoreProps = {
  ...BaseModelStoreProps,
  simulations: types.optional(types.array(types.frozen()), []),

  queryingSimulation: types.maybeNull(types.boolean),
  syncingSimulation: types.maybeNull(types.boolean),
  syncedSimulation: types.maybeNull(types.boolean),

  search: types.maybeNull(types.string),
  simulation: types.optional(types.frozen(), {}),
  prices: types.optional(types.array(types.frozen()), []),
  selectedRegion: types.maybeNull(types.number)
}

/**
 * Model description here for TypeScript hints.
 */
export const SimulationStoreModel = types
  .model("SimulationStore")
  .props(SimulationStoreProps)
  .extend(withEnvironment)
  .extend(withRootStore)
  .extend(withBaseModel)
  .views((self) => ({
    get loadingSimulation() {
      return self.queryingSimulation || self.syncingSimulation
    },
  })) // eslint-disable-line @typescript-eslint/no-unused-vars
  .actions((self) => ({
    set(key: keyof typeof SimulationStoreProps, value: any) {
      if (self[key] === value) return
      self[key] = value as never;
    },
    init() {
      self.initModel();
      this.getSimulations().catch()
    },
    async getSimulations(sync = true, onFinishSync = undefined) {
      if (!sync) {
        this.set('queryingSimulation', true);
        const models = await Simulation.fetch({
          search: self.search,
          with: 'user',
          sort: 'created_at,desc'
        }).catch((e) => {
          console.warn(e)
          return []
        })
        this.set('simulations', models.map((model) => model.toArray()));
        this.set('queryingSimulation', false);
      }
      else {
        this.set('queryingSimulation', true);
        this.syncSimulations().then(async () => {
          await this.getSimulations(false).catch()
          onFinishSync && onFinishSync()
          this.set('syncedSimulation', true);
          this.set('queryingSimulation', false);
        })
      }
    },
    async syncSimulations() {
      if (Platform.OS === 'web') return true;

      this.set('syncingSimulation', true)
      const success = await Simulation.syncAll(
        {},
        [], true, undefined, (e) => {
          if (e.message && !Toast.isActive(e.name)) Toast.show({ id: e.name, title: e.message });
        }
      )

      this.set('syncingSimulation', false)
      return success
    },
    simulationToParams(simulation) {
      return {
        id: simulation.id,
        name: simulation.name,
        seed: simulation.total_seed,
        date: simulation.started_at,
        wide: simulation.area,
        biomass: (simulation.additional_data || {}).limit_weight_per_area,
        periode: (simulation.additional_data || {}).total_cultivation_day,
        size: simulation.size_at_100_days,
        survival: simulation.survival_rate,
        feed: simulation.feed_price,
        shrimpPrice: (simulation.additional_data || {}).shrimp_prices,
        currency: (simulation.additional_data || {}).currency_id,
        plan: (simulation.additional_data || {}).harvest_plans,
      }
    },
    async calculateSimulation(params) {
      return ({
        ...{
          id: params.id || null,
          name: params.name || `Simulation ${moment().format('YYYY-MM-DD')}`,
          area: params.wide || params.area || 1000,
          totalSeed: params.seed || params.totalSeed || params.total_seed || 100000,
          startedAt: params.date || params.startedAt || params.started_at || moment().format('YYYY-MM-DD'),
          maxBiomassArea: params.biomass
            || params.maxBiomassArea
            || params.additional_data?.limit_weight_per_area || 1.2,
          totalCultivationDay: params.periode
            || params.totalCultivationDay
            || params.additional_data?.total_cultivation_day
            || 120,
          currency: (typeof params.currency === 'object' && params.currency) || null,
          currencyId: params.currency || params.currencyId || params.additional_data?.currency_id || null,
          partialPlans: (
            params.plan
              && params.plan.length
              && params.plan.map(({ age, percent }) => ({ age, percent }))
          ) || (
            params.partialPlans
              && params.partialPlans.length
              && params.partialPlans.map(({ age, percent }) => ({ age, percent }))
          ) || (
            params.additional_data?.harvest_plans
              && (params.additional_data?.harvest_plans || []).length
              && (params.additional_data?.harvest_plans || [])
                .map(({ age, percent }) => ({ age, percent }))
          ) || [],
          feedPrice: params.feed || params.feedPrice || params.feed_price || 15000,
          shrimpPriceId: params.shrimpPrice?.id
            || params.shrimpPriceId
            || params.additional_data?.shrimp_prices?.id || null,
          currencyShrimpPrice: params.currencyShrimpPrice || null,
          defaultPrices: [
            { size: 200, price: null },
            { size: 190, price: null },
            { size: 180, price: null },
            { size: 170, price: null },
            { size: 160, price: null },
            { size: 150, price: null },
            { size: 140, price: null },
            { size: 130, price: null },
            { size: 120, price: null },
            { size: 110, price: null },
            { size: 100, price: null },
            { size: 90, price: null },
            { size: 80, price: null },
            { size: 70, price: null },
            { size: 60, price: null },
            { size: 50, price: null },
            { size: 40, price: null },
            { size: 30, price: null },
            { size: 20, price: null }
          ],
          prices: [
            { size: 200, price: null },
            { size: 190, price: null },
            { size: 180, price: null },
            { size: 170, price: null },
            { size: 160, price: null },
            { size: 150, price: null },
            { size: 140, price: null },
            { size: 130, price: null },
            { size: 120, price: null },
            { size: 110, price: null },
            { size: 100, price: null },
            { size: 90, price: null },
            { size: 80, price: null },
            { size: 70, price: null },
            { size: 60, price: null },
            { size: 50, price: null },
            { size: 40, price: null },
            { size: 30, price: null },
            { size: 20, price: null }
          ].map(({ size, price }) => ({
            size,
            price: (
              params.shrimpPrice?.prices
              || params.prices
              || params.additional_data?.shrimp_prices?.prices
              || []
            )
              .find((p) => p.size === size)?.price || price
          })),
          survivalRatePercent: params.survival
            || params.survivalRatePercent
            || params.survival_rate || 100,
          size100Day: params.size || params.size100Day || params.size_at_100_days || 45,

          getPriceBySize(size) {
            const prices = _.orderBy(
              _.filter(this.prices,
                (price) => (price.size < (size + 10) && (size - 10) <= price.size) && price.price),
              (price) => price.size
            );
            if (prices.length < 2) {
              if (prices.length === 1 && prices[0].size === size) return prices[0].price;
              return 0;
            }
            return (
              ((prices[0].price - prices[1].price) * (size - prices[1].size))
                / (prices[0].size - prices[1].size)
            ) + prices[1].price;
          },
          get harvestPlans() {
            if (this._harvestPlans) return this._harvestPlans;
            const totalPartialPlansPercent = _.sum(
              _.map(
                this.partialPlans, (partialPlan) => Number(partialPlan.percent || 0)
              )
            );
            // this.$validator.validate('last_harvest_plan[percent]', 100 - totalPartialPlansPercent);
            const harvestPlans = _.map(this.partialPlans, (partialPlan) => ({
              age: Number(partialPlan.age || 0),
              percent: Number(partialPlan.percent || 0)
            }));
            harvestPlans.push({
              age: this.totalCultivationDay,
              percent: 100 - totalPartialPlansPercent
            });
            this._harvestPlans = _.orderBy(harvestPlans, (harvestPlan) => harvestPlan.age);
            return this._harvestPlans;
          },
          get survivalRate() {
            if (this._survivalRate) return this._survivalRate;
            this._survivalRate = this.survivalRatePercent / 100;
            return this._survivalRate;
          },
          get cultivationDates() {
            if (this._cultivationDates) return this._cultivationDates;
            this._cultivationDates = _.map(
              Array(Number(this.totalCultivationDay) + 1),
              (a, index) => moment(this.startedAt).add(index, 'd').format('YYYY-MM-DD')
            );
            return this._cultivationDates;
          },
          get growthConstant() {
            if (this._growthConstant) return this._growthConstant;
            const a = 0.001597937825;
            const b = 0.0682293375;
            const c = 0.007344587;

            // eslint-disable-next-line no-restricted-properties
            this._growthConstant = (1000 / this.size100Day) / ((a * Math.pow(100, 2)) + (b * 100) + c);
            return this._growthConstant;
          },
          get massGrowth() {
            if (this._massGrowth) return this._massGrowth;
            const { cultivationDates, growthConstant } = this;
            this._massGrowth = _.map(cultivationDates, (cultivationDate, age: number) => {
              const a = 0.001597937825;
              const b = 0.0682293375;
              const c = 0.007344587;

              // eslint-disable-next-line no-restricted-properties
              const massGrowth = growthConstant * ((a * Math.pow(age, 2)) + (b * age) + c);
              return _.round(massGrowth, 2);
            });
            return this._massGrowth;
          },
          get totalHarvestQuantityData() {
            if (this._totalHarvestQuantityData) return this._totalHarvestQuantityData;
            const { cultivationDates, harvestPlans, survivorQuantity } = this;
            this._totalHarvestQuantityData = _.map(
              cultivationDates,
              (cultivationDate, age: number) => (_.sum(_.map(
                _.filter(harvestPlans, (harvestPlan) => Number(harvestPlan.age || 0) <= age),
                (harvestPlan) => Number(harvestPlan.percent || 0)
              )) * survivorQuantity[age]) / 100
            );
            return this._totalHarvestQuantityData;
          },
          get survivorQuantity() {
            if (this._survivorQuantity) return this._survivorQuantity;
            const { cultivationDates, survivalRate, totalSeedPerArea } = this;
            this._survivorQuantity = _.map(
              cultivationDates,
              (cultivationDate, age: number) => (
                totalSeedPerArea * (1 - (((1 - survivalRate) * age) / this.totalCultivationDay))
              )
            );
            return this._survivorQuantity;
          },
          get totalMassAliveData() {
            if (this._totalMassAliveData) return this._totalMassAliveData;
            const {
              cultivationDates, survivorQuantity, totalHarvestQuantityData, massGrowth
            } = this;
            // if (this.errors.any()) {
            //   return _.map(this.cultivationDates, () => 0);
            // }
            this._totalMassAliveData = _.map(
              cultivationDates,
              (cultivationDate, age) => (
                this.area
                          * (survivorQuantity[age] - totalHarvestQuantityData[age])
                          * massGrowth[age]
              ) / 1000000
            );
            return this._totalMassAliveData;
          },
          get totalMassAlivePerfectData() {
            if (this._totalMassAlivePerfectData) return this._totalMassAlivePerfectData;
            const {
              cultivationDates, totalSeedPerArea, totalHarvestQuantityData, massGrowth
            } = this;
            // if (this.errors.any()) {
            //   return _.map(this.cultivationDates, () => 0);
            // }
            this._totalMassAlivePerfectData = _.map(
              cultivationDates,
              (cultivationDate, age) => (
                this.area
                          * (totalSeedPerArea - totalHarvestQuantityData[age])
                          * massGrowth[age]
              ) / 1000000
            );
            return this._totalMassAlivePerfectData;
          },
          get feedRatioData() {
            if (this._feedRatioData) return this._feedRatioData;
            const { totalMassAliveData, massGrowth } = this;
            this._feedRatioData = _.map(totalMassAliveData, (totalMassAlive, age: number) => {
              const a = 11.286539;
              const b = -0.51239506;

              // eslint-disable-next-line no-restricted-properties
              return (a * Math.pow(massGrowth[age], b)) / 100;
            });
            return this._feedRatioData;
          },
          get feedData() {
            if (this._feedData) return this._feedData;
            const { feedRatioData, totalSeedPerArea, totalMassAliveData } = this;
            this._feedData = _.map(feedRatioData, (feedRatio, age: number) => {
              let feed;
              if (age <= 10) {
                feed = ((3 + (0.2 * age)) * totalSeedPerArea * this.area) / 100000;
              } else if (age <= 20) {
                feed = ((3 + 2 + (0.4 * (age - 10))) * totalSeedPerArea * this.area) / 100000;
              } else if (age <= 30) {
                feed = ((3 + 2 + 4 + (0.8 * (age - 20))) * totalSeedPerArea * this.area) / 100000;
              } else {
                feed = totalMassAliveData[age] * 1000 * feedRatio;
              }
              return _.round(feed, 2);
            });
            return this._feedData;
          },
          get feedPerfectData() {
            if (this._feedPerfectData) return this._feedPerfectData;
            const { feedRatioData, totalSeedPerArea, totalMassAlivePerfectData } = this;
            this._feedPerfectData = _.map(feedRatioData, (feedRatio, age: number) => {
              let feed;
              if (age <= 10) {
                feed = ((3 + (0.2 * age)) * totalSeedPerArea * this.area) / 100000;
              } else if (age <= 20) {
                feed = ((3 + 2 + (0.4 * (age - 10))) * totalSeedPerArea * this.area) / 100000;
              } else if (age <= 30) {
                feed = ((3 + 2 + 4 + (0.8 * (age - 20))) * totalSeedPerArea * this.area) / 100000;
              } else {
                feed = totalMassAlivePerfectData[age] * 1000 * feedRatio;
              }
              return _.round(feed, 2);
            });
            return this._feedPerfectData;
          },
          get totalFeedData() {
            if (this._totalFeedData) return this._totalFeedData;
            const { feedData } = this;
            this._totalFeedData = _.map(feedData, (feed, age: number) => _.sum(_.slice(feedData, 0, age + 1)) / 1000);
            return this._totalFeedData;
          },
          get totalFeedPerfectData() {
            if (this._totalFeedPerfectData) return this._totalFeedPerfectData;
            const { feedPerfectData } = this;
            this._totalFeedPerfectData = _.map(
              feedPerfectData,
              (feed, age: number) => _.sum(_.slice(feedPerfectData, 0, age + 1)) / 1000
            );
            return this._totalFeedPerfectData;
          },
          get harvests() {
            if (this._harvests) return this._harvests;
            const {
              harvestPlans, survivorQuantity, massGrowth, cultivationDates, totalMassAliveData
            } = this;
            this._harvests = _.map(harvestPlans, (harvestPlan) => {
              const mass = Math.floor(
                (
                  this.area
                              * ((survivorQuantity[harvestPlan.age] * harvestPlan.percent) / 100)
                              * massGrowth[harvestPlan.age]
                ) / 1000
              );
              const size = Math.floor(1000 / massGrowth[harvestPlan.age]);
              return {
                age: harvestPlan.age,
                date: cultivationDates[harvestPlan.age],
                mass,
                size,
                totalPrice: mass * this.getPriceBySize(size),
                notes: (totalMassAliveData[harvestPlan.age - 1] < ((1.2 / 1000) * this.area)) ? null : '*'
              };
            });
            return this._harvests;
          },
          get totalHarvest() {
            if (this._totalHarvest) return this._totalHarvest;
            const { harvests } = this;
            this._totalHarvest = {
              mass: _.sum(_.map(harvests, (harvest) => harvest.mass)),
              totalPrice: _.sum(_.map(harvests, (harvest) => harvest.totalPrice))
            };
            return this._totalHarvest;
          },
          get totalSeedPerArea() {
            if (this._totalSeedPerArea) return this._totalSeedPerArea;
            this._totalSeedPerArea = this.totalSeed / this.area;
            return this._totalSeedPerArea;
          }
        } as any
      })
    },

    async saveSimulation() {
      const { simulation, prices, selectedRegion } = self;
      const { tokenStore: { token } } = self.rootStore
      this.set('loading', true)
      const body = {
        name: simulation.name,
        total_seed: simulation.seed,
        started_at: simulation.date,
        survival_rate: simulation.survival,
        size_at_100_days: simulation.size,
        feed_price: simulation.feed,
        area: simulation.area,
        additional_data: {
          total_cultivation_day: simulation.periode,
          harvest_plans: simulation.plan,
          limit_weight_per_area: simulation.biomass,
          shrimp_prices: {
            id: selectedRegion,
            prices: prices
          },
          currency_id: simulation.currencyId
        }
      };
      const result: any = await self.apiSauce.post("/simulations", body, {
        headers: {
          Authorization : `Bearer ${token.access_token}`
        }
      })
      if (result.status === 201) {
        JalaAnalytic.log('Mobile app: simulation - created');

        this.set('error', null)
        this.set('needRefresh', true)
      } else {
        this.set('error', getGeneralApiProblem(result))
      }
      this.set('loading', false);
      this.set('prices', [])
      this.set('selectedRegion', null)
      return result
    },

    async editSimulation() {
      const { simulation, prices, selectedRegion } = self;
      const { tokenStore: { token } } = self.rootStore
      this.set('loading', true)
      const body = {
        id: simulation.id,
        name: simulation.name,
        total_seed: simulation.seed,
        started_at: simulation.date,
        survival_rate: simulation.survival,
        size_at_100_days: simulation.size,
        feed_price: simulation.feed,
        area: simulation.area,
        additional_data: {
          total_cultivation_day: simulation.periode,
          harvest_plans: simulation.plan,
          limit_weight_per_area: simulation.biomass,
          shrimp_prices: {
            id: selectedRegion,
            prices: prices
          },
          currency_id: simulation.currencyId
        }
      };
      const result: any = await self.environment.api.apisauce.put(`/simulations/${body.id}`, body, {
        headers: {
          Authorization : `Bearer ${token.access_token}`,
        }
      })
      if (result.status === 200) {
        JalaAnalytic.log('Mobile app: simulation - edited');

        this.set('needRefresh', true)
        this.set('error', null)
      } else {
        this.set('error', getGeneralApiProblem(result))
      }
      this.set('loading', false);
      this.set('prices', [])
      this.set('selectedRegion', null)
      return result
    },
    async deleteSimulation (id: number) {
      const { tokenStore: { token } } = self.rootStore
      this.set('loading', true)
      const result: any = await self.environment.api.apisauce.delete(`/simulations/${id}`, {}, {
        headers: {
          Authorization : `Bearer ${token.access_token}`
        }
      })
      if (result.status === 200) {
        JalaAnalytic.log('Mobile app: simulation - deleted');

        this.set('error', null)
        this.set('needRefresh', true)
      } else {
        this.set('error', getGeneralApiProblem(result))
      }
      this.set('loading', false);
    }
  })) // eslint-disable-line @typescript-eslint/no-unused-vars

type SimulationStoreType = Instance<typeof SimulationStoreModel>
export interface SimulationStore extends SimulationStoreType {}
type SimulationStoreSnapshotType = SnapshotOut<typeof SimulationStoreModel>
export interface SimulationStoreSnapshot extends SimulationStoreSnapshotType {}
export const createSimulationStoreDefaultModel = () => types.optional(SimulationStoreModel, {})
